import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Trash2, User, Trophy, Calendar, PlayCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../../components/common/Logo';

interface Child {
  id: string;
  _id: string;
  firstName: string;
  lastName: string;
  name: string;
  sports: string[];
  dateOfBirth: string;
  age: number | null;
  avatar: string | null;
  bio?: string;
  status: string;
  createdAt: string;
}

const SPORTS = ['chess', 'cricket', 'tennis', 'football', 'badminton', 'basketball', 'swimming', 'athletics'];

const ParentDashboardPage: React.FC = () => {
  const { user, logout, actAsChild } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState<Child[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [draft, setDraft] = useState({ firstName: '', lastName: '', dateOfBirth: '', sport: '' });

  const playAsChild = async (childId: string, childName: string) => {
    setSwitchingId(childId);
    try {
      await actAsChild(childId);
      toast.success(`Now acting as ${childName}`);
      navigate('/home');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || err?.message || 'Switch failed');
      setSwitchingId(null);
    }
  };

  const loadChildren = async () => {
    try {
      const resp = await api.get('/users/children');
      if (resp.data.success) setChildren(resp.data.data.children);
    } catch (err: any) {
      const code = err?.response?.data?.error?.code;
      if (code === 'PARENT_REQUIRED') {
        toast.error('This page is for parent accounts only');
        navigate('/home');
        return;
      }
      toast.error('Failed to load children');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadChildren(); }, []);

  const addChild = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.firstName || !draft.lastName || !draft.dateOfBirth || !draft.sport) {
      toast.error('Please fill all fields');
      return;
    }
    setSaving(true);
    try {
      const resp = await api.post('/users/children', {
        firstName: draft.firstName,
        lastName: draft.lastName,
        dateOfBirth: draft.dateOfBirth,
        sports: [draft.sport],
      });
      if (resp.data.success) {
        toast.success('Child profile added');
        setDraft({ firstName: '', lastName: '', dateOfBirth: '', sport: '' });
        setShowAdd(false);
        loadChildren();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Failed to add child');
    } finally {
      setSaving(false);
    }
  };

  const removeChild = async (id: string, name: string) => {
    if (!window.confirm(`Remove ${name}'s profile? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/children/${id}`);
      toast.success('Profile removed');
      loadChildren();
    } catch {
      toast.error('Failed to remove');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple parent header - kept separate from the player UserLayout because
          parent role doesn't have a feed/community view in the MVP. */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center hover:opacity-90">
            <Logo variant="dark" className="h-9 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-sm text-gray-300">
              {user?.firstName ? `Hi, ${user.firstName}` : 'Parent account'}
            </span>
            <button
              onClick={() => { logout(); navigate('/'); }}
              className="text-sm bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full transition-colors"
            >
              Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Your children</h1>
            <p className="text-gray-600 text-sm mt-1">
              Manage each child&apos;s profile. They cannot log in themselves.
            </p>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="bg-gray-900 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-gray-800 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add child
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading...</div>
        ) : children.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center">
            <User className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 mb-4">No child profiles yet.</p>
            <button
              onClick={() => setShowAdd(true)}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-800"
            >
              Add your first child
            </button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {children.map((c) => (
              <div key={c.id} className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-14 h-14 rounded-full bg-pink-500 text-white flex items-center justify-center text-xl font-bold overflow-hidden">
                    {c.avatar ? (
                      <img src={c.avatar} alt={c.name} className="w-full h-full object-cover" />
                    ) : (
                      c.firstName[0] + c.lastName[0]
                    )}
                  </div>
                  <button
                    onClick={() => removeChild(c.id, c.name)}
                    className="text-gray-400 hover:text-red-500 p-1"
                    title="Remove profile"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <h3 className="text-lg font-bold text-gray-900">{c.name}</h3>
                <div className="mt-2 space-y-1 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-gray-400" />
                    <span>{c.sports.join(', ') || '—'}</span>
                  </div>
                  {c.age !== null && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span>{c.age} years old</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => playAsChild(c.id, c.name)}
                  disabled={switchingId === c.id}
                  className="mt-4 w-full bg-gray-900 text-white py-2 rounded-lg font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <PlayCircle className="w-4 h-4" />
                  {switchingId === c.id ? 'Switching...' : `Act as ${c.firstName}`}
                </button>
              </div>
            ))}
          </div>
        )}
      </main>

      {showAdd && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Add child profile</h2>
              <button onClick={() => setShowAdd(false)} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">×</button>
            </div>
            <form onSubmit={addChild} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="First name"
                  value={draft.firstName}
                  onChange={(e) => setDraft({ ...draft, firstName: e.target.value })}
                  className="input-field"
                  required
                />
                <input
                  type="text"
                  placeholder="Last name"
                  value={draft.lastName}
                  onChange={(e) => setDraft({ ...draft, lastName: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Date of birth</label>
                <input
                  type="date"
                  value={draft.dateOfBirth}
                  onChange={(e) => setDraft({ ...draft, dateOfBirth: e.target.value })}
                  max={new Date().toISOString().slice(0, 10)}
                  className="input-field mt-1"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Sport</label>
                <select
                  value={draft.sport}
                  onChange={(e) => setDraft({ ...draft, sport: e.target.value })}
                  className="input-field mt-1"
                  required
                >
                  <option value="">Pick a sport</option>
                  {SPORTS.map(s => (<option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>))}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAdd(false)} className="flex-1 bg-gray-100 text-gray-800 py-2.5 rounded-lg font-medium hover:bg-gray-200">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="flex-1 bg-gray-900 text-white py-2.5 rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50">
                  {saving ? 'Saving...' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentDashboardPage;
